/**
 * Utility functions for column configuration management
 */

/**
 * Filters columns based on country code
 * @param {Array} columns - Array of column configurations
 * @param {string} countryCode - The country code to filter by
 * @returns {Array} Filtered array of columns
 */
export const filterColumnsByCountry = (columns, countryCode) => {
    return columns.filter(column => {
        // If column has no countryCode specified, include it for all countries
        if (!column.countryCode) {
            return true;
        }
        
        // If column has countryCode specified, only include it for that specific country
        return column.countryCode === countryCode;
    });
};

/**
 * Gets the filter type from column configuration
 * @param {Object} column - Column configuration object
 * @returns {string} Filter type (defaults to 'text')
 */
export const getColumnFilterType = (column) => {
    return column?.filterType || 'text';
};

/**
 * Sets default filter values for columns that have defaultFilterValue configured
 * @param {Array} columns - Array of column configurations
 * @param {Array} searchArr - Current search/filter array
 * @param {Function} setSearchArry - Function to update search array
 */
export const setDefaultFilterValues = (columns, searchArr, setSearchArry) => {
    columns.forEach(column => {
        if (column.defaultFilterValue && column.filterable !== false) {
            const hasFilter = searchArr.some(filter => filter.field === column.name_of_db_column);
            if (!hasFilter) {
                setSearchArry({
                    field: column.name_of_db_column,
                    value: column.defaultFilterValue,
                    type: getColumnFilterType(column)
                });
            }
        }
    });
};

/**
 * Filters columns based on feature flags and country code
 * This is the centralized function that handles all column visibility logic
 * @param {Array} columns - Array of column configurations
 * @param {string} countryCode - The country code to filter by
 * @param {Object} featureFlags - Object containing feature flag functions
 * @returns {Array} Filtered array of columns
 */
export const filterColumnsByFeatures = (columns, countryCode, featureFlags = {}) => {
    return columns.filter(column => {
        // Handle country-specific columns (legacy support)
        if (column.countryCode) {
            return column.countryCode === countryCode;
        }
        
        // Handle feature-specific columns
        if (column.featureFlag) {
            const featureFunction = featureFlags[column.featureFlag];
            if (featureFunction && typeof featureFunction === 'function') {
                return featureFunction(countryCode);
            }
        }
        
        // Include columns that don't have any specific filtering requirements
        return true;
    });
};
